import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BookOpen, Database, User, Sparkles } from 'lucide-react';
import { clsx } from 'clsx';
import WorkspacePill from '../../components/layout/WorkspacePill';
import BusinessFilter from '../../components/layout/BusinessFilter';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { wikiApi, type WikiTree, type WikiPageView } from '../../services/wiki-api';
import ReadView from './ReadView';
import EditView from './EditView';
import HistoryView from './HistoryView';
import DiscussView from './DiscussView';

export type WikiViewTab = 'read' | 'edit' | 'history' | 'discuss';

const ARCHETYPE_LABEL: Record<string, string> = {
  business_profile: 'Business profile',
  data_source_profile: 'Data source wiki',
  routine_profile: 'Routine wiki',
  user_preferences: 'Preferences',
};

const WikiPage: React.FC = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = (searchParams.get('view') as WikiViewTab | null) ?? 'read';
  const interview = searchParams.get('mode') === 'interview';

  const { data: tree, isLoading: treeLoading } = useQuery({
    queryKey: ['wikiTree'],
    queryFn: () => wikiApi.getTree(),
  });

  const { data: page, isLoading: pageLoading, isError } = useQuery({
    queryKey: ['wikiPage', pageId],
    queryFn: () => wikiApi.getPage(pageId!),
    enabled: !!pageId,
  });

  // The tree spans the workspace; this narrows it (§8, page-local). People pages
  // are global to the identity, so they survive every business filter.
  const [orgFilter, setOrgFilter] = useState<string | null>(null);
  const visibleTree = useMemo<WikiTree | undefined>(
    () =>
      tree && orgFilter
        ? { ...tree, businesses: tree.businesses.filter((b) => b.orgId === orgFilter) }
        : tree,
    [tree, orgFilter],
  );
  const treeOrgs = useMemo(
    () => (tree?.businesses ?? []).map((b) => ({ id: b.orgId, name: b.orgName })),
    [tree],
  );

  // No page selected → jump to the first page in the tree.
  useEffect(() => {
    if (pageId || !visibleTree) return;
    const first =
      visibleTree.businesses.find((b) => b.profile)?.profile?.pageId ??
      visibleTree.businesses.find((b) => b.sources.length > 0)?.sources[0]?.pageId ??
      visibleTree.people[0]?.pageId;
    if (first) navigate(`/wiki/${first}`, { replace: true });
  }, [pageId, visibleTree, navigate]);

  const setView = (v: WikiViewTab): void => {
    const next = new URLSearchParams(searchParams);
    if (v === 'read') next.delete('view');
    else next.set('view', v);
    if (v !== 'discuss') next.delete('mode');
    setSearchParams(next, { replace: true });
  };

  const activeView: WikiViewTab = page && !page.canEdit && (view === 'edit' || view === 'discuss') ? 'read' : view;

  return (
    <div className="max-w-[82rem] mx-auto px-6 pt-7 pb-24">
      <div className="flex items-start justify-between gap-4">
        <div className="text-[12.5px] text-muted-foreground pt-1">
          <Link to="/businesses" className="text-primary hover:underline">
            Businesses
          </Link>
          {page && <> / {page.orgName} / </>}
          <b className="text-foreground">Wiki</b>
        </div>
        <div className="flex items-center gap-2.5">
          <BusinessFilter value={orgFilter} onChange={setOrgFilter} orgs={treeOrgs} />
          <WorkspacePill />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[246px_minmax(0,1fr)] gap-3.5 mt-3.5 items-start">
        <Tree tree={visibleTree} loading={treeLoading} activePageId={pageId} />

        <div className="rounded-[10px] border border-border bg-card shadow-sm overflow-hidden min-h-[420px]">
          {pageLoading || (!page && !isError) ? (
            <div className="flex items-center justify-center min-h-[40vh]">
              <LoadingSpinner />
            </div>
          ) : isError || !page ? (
            <div className="p-10 text-center text-[13px] text-muted-foreground">
              This wiki page doesn&apos;t exist or you don&apos;t have access to it.
            </div>
          ) : (
            <>
              <DocHead page={page} />
              <ViewTabs page={page} active={activeView} onChange={setView} />
              {activeView === 'read' && <ReadView page={page} onReview={() => setView('history')} />}
              {activeView === 'edit' && <EditView page={page} onDone={() => setView('read')} />}
              {activeView === 'history' && <HistoryView page={page} />}
              {activeView === 'discuss' && <DiscussView page={page} interview={interview} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Doc header ──────────────────────────────────────────────────────────

const DocHead: React.FC<{ page: WikiPageView }> = ({ page }) => (
  <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-border">
    <span className="w-[30px] h-[30px] rounded-lg bg-muted border border-border flex items-center justify-center text-muted-foreground flex-none">
      {page.archetype === 'data_source_profile' ? (
        <Database className="w-4 h-4" />
      ) : page.archetype === 'user_preferences' ? (
        <User className="w-4 h-4" />
      ) : (
        <BookOpen className="w-4 h-4" />
      )}
    </span>
    <div className="min-w-0">
      <div className="text-[15px] font-semibold text-foreground truncate">{page.title}</div>
      <div className="text-[10.5px] text-muted-foreground mt-px">
        {ARCHETYPE_LABEL[page.archetype] ?? 'Wiki page'} · v{page.version} · maintained by Zamino AI
        {' '}· last edit{' '}
        {new Date(page.updatedAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
      </div>
    </div>
  </div>
);

const ViewTabs: React.FC<{
  page: WikiPageView;
  active: WikiViewTab;
  onChange: (v: WikiViewTab) => void;
}> = ({ page, active, onChange }) => {
  const tab = (v: WikiViewTab, label: React.ReactNode, extra?: string): React.ReactNode => (
    <button
      type="button"
      onClick={() => onChange(v)}
      className={clsx(
        'px-3 py-2.5 text-[13px] font-medium -mb-px border-b-2 inline-flex items-center gap-1.5',
        active === v
          ? 'text-primary border-primary font-semibold bg-card'
          : 'text-muted-foreground border-transparent hover:text-foreground',
        extra,
      )}
    >
      {label}
    </button>
  );

  return (
    <div className="flex gap-0.5 px-5 border-b border-border bg-muted/40">
      {tab('read', 'Read')}
      {page.canEdit && tab('edit', 'Edit')}
      {tab(
        'history',
        <>
          History
          {page.unseen.count > 0 && (
            <span className="text-[9.5px] font-bold bg-info/15 text-info rounded-full px-1.5 py-px">
              {page.unseen.count} new
            </span>
          )}
        </>,
      )}
      {page.canEdit && tab('discuss', <>✦ Discuss with AI</>, 'text-primary')}
    </div>
  );
};

// ── Tree ────────────────────────────────────────────────────────────────

const Tree: React.FC<{
  tree: WikiTree | undefined;
  loading: boolean;
  activePageId: string | undefined;
}> = ({ tree, loading, activePageId }) => (
  <div className="rounded-[10px] border border-border bg-card shadow-sm py-2 text-[12.5px] lg:sticky lg:top-5">
    {loading || !tree ? (
      <div className="flex items-center justify-center py-10">
        <LoadingSpinner />
      </div>
    ) : (
      <>
        {tree.businesses.map((b) => (
          <div key={b.orgId}>
            <div className="flex items-center gap-2 px-3.5 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              <span className="w-[18px] h-[18px] rounded-[5px] bg-primary/15 text-primary flex items-center justify-center text-[7.5px] font-bold tracking-normal">
                {monogram(b.orgName)}
              </span>
              {b.orgName}
            </div>
            {b.profile && (
              <TreeItem
                pageId={b.profile.pageId}
                active={activePageId === b.profile.pageId}
                icon={<BookOpen className="w-[11px] h-[11px]" />}
                iconClass="bg-primary/15 text-primary"
                label={b.profile.title}
                bold
                pending={b.profile.pending}
                version={b.profile.version}
              />
            )}
            {b.sources.length > 0 && (
              <div className="text-[8.5px] font-bold uppercase tracking-widest text-muted-foreground pt-1.5 pb-0.5 pl-11">
                Sources
              </div>
            )}
            {b.sources.map((s) => (
              <TreeItem
                key={s.pageId}
                pageId={s.pageId}
                active={activePageId === s.pageId}
                icon={<Database className="w-[11px] h-[11px]" />}
                iconClass="bg-muted text-muted-foreground border border-border"
                label={s.sourceName}
                indent
                pending={s.pending}
                version={s.version}
              />
            ))}
          </div>
        ))}

        {tree.people.length > 0 && (
          <div>
            <div className="flex items-center gap-2 px-3.5 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              <span className="w-[18px] h-[18px] rounded-[5px] bg-success/15 text-success flex items-center justify-center">
                <Sparkles className="w-2.5 h-2.5" />
              </span>
              People
            </div>
            {tree.people.map((p) => (
              <TreeItem
                key={p.pageId}
                pageId={p.pageId}
                active={activePageId === p.pageId}
                icon={<User className="w-[11px] h-[11px]" />}
                iconClass="bg-success/15 text-success"
                label={`${p.name} — Preferences`}
                pending={p.pending}
                version={p.version}
              />
            ))}
          </div>
        )}
      </>
    )}
  </div>
);

const TreeItem: React.FC<{
  pageId: string;
  active: boolean;
  icon: React.ReactNode;
  iconClass: string;
  label: string;
  bold?: boolean;
  indent?: boolean;
  pending: boolean;
  version: number;
}> = ({ pageId, active, icon, iconClass, label, bold, indent, pending, version }) => (
  <Link
    to={`/wiki/${pageId}`}
    className={clsx(
      'flex items-center gap-2 py-1.5 pr-3.5 min-w-0',
      indent ? 'pl-9' : 'pl-[18px]',
      active ? 'bg-accent text-primary font-semibold' : 'text-foreground hover:bg-muted',
      bold && !active && 'font-semibold',
    )}
  >
    <span className={clsx('w-5 h-5 rounded-md flex items-center justify-center flex-none', iconClass)}>
      {icon}
    </span>
    <span className="truncate">{label}</span>
    {pending ? (
      <span className="ml-auto text-[8.5px] font-extrabold text-warn flex-none">PENDING</span>
    ) : (
      <span className="ml-auto text-[9.5px] text-muted-foreground flex-none">v{version}</span>
    )}
  </Link>
);

function monogram(name: string): string {
  const words = name.trim().split(/\s+/);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || '?';
}

export default WikiPage;
